import { useMemo } from "react";
import { cn } from "@/utils/cn";
import { grcCss } from "@/utils/grcCss";
import type { GRCReportGridResponse, ProgramDetailDto, GRCActiveFilters } from "@/types/grc";

// Left-column widths (must match sticky offsets below)
const COL_LINK = 40;   // px
const COL_STATUS = 27; // px
const COL_NAME = 210;  // px
const COL_ID = 58;     // px

// Cumulative offsets
const OFFSET_STATUS = COL_LINK;
const OFFSET_NAME = COL_LINK + COL_STATUS;
const OFFSET_ID = COL_LINK + COL_STATUS + COL_NAME;

interface GRCGridProps {
  data: GRCReportGridResponse;
  filters: GRCActiveFilters;
  showPickups: boolean;
  showDatesAtBottom: boolean;
  highlightEventName?: string;
  onEventNameDoubleClick: (program: ProgramDetailDto) => void;
  onDraftEventClick: (program: ProgramDetailDto) => void;
}

/**
 * GRC Room-Block Grid.
 *
 * Structure:
 *   ┌──────────────────────────────────────────────────── (sticky left) ─┬─ (scrollable) ─────────
 *   │ Link │ Status │ Event Name (210px) │ ID │  day-1 │ day-2 │ … │ day-31
 *   ├──────┴────────┴────────────────────┴────┼─────────────────────────
 *   │ …rows…                                  │
 *   ├─────────────────────────────────────────┤
 *   │ Blocked footer │ …per-day blocked counts│
 *   │ Available footer│ …per-day available     │
 *   └─────────────────────────────────────────┘
 */
export function GRCGrid({
  data,
  filters,
  showPickups,
  showDatesAtBottom,
  highlightEventName,
  onEventNameDoubleClick,
  onDraftEventClick
}: GRCGridProps) {
  const filteredPrograms = useMemo(() => {
    return data.programDetails.filter((p) => {
      if (filters.eventTypes.length > 0 && !filters.eventTypes.includes(p.eventType)) return false;
      if (filters.statuses.length > 0 && !filters.statuses.includes(p.eventStatus)) return false;
      return true;
    });
  }, [data.programDetails, filters]);

  const days = data.daysOfMonth;

  if (filteredPrograms.length === 0 && days.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-slate-500">No data available for this month.</p>
    );
  }

  return (
    <div className="overflow-x-auto" style={{ height: "calc(93vh - 150px)" }}>
      <table className="w-max border-collapse text-xs" aria-label="GRC room block grid">
        <thead className="sticky top-0 z-30 bg-[#E8E8E8]">
          <tr>
            {/* ── Sticky left columns ── */}
            <StickyTh width={COL_LINK} left={0} className="bg-[#E8E8E8]" />
            <StickyTh width={COL_STATUS} left={OFFSET_STATUS} className="bg-[#E8E8E8] text-center">
              --
            </StickyTh>
            <StickyTh width={COL_NAME} left={OFFSET_NAME} className="bg-[#E8E8E8] text-left pl-1 font-semibold text-black">
              Event Name
            </StickyTh>
            <StickyTh width={COL_ID} left={OFFSET_ID} className="bg-[#E8E8E8] text-center font-semibold text-black">
              ID
            </StickyTh>

            {/* ── Day columns ── */}
            {days.map((d) => (
              <th
                key={d.day}
                scope="col"
                className={cn(
                  "w-[38px] border-l border-slate-200 px-0.5 py-1 text-center font-normal",
                  grcCss(d.holidayHeaderCSSClass)
                )}
                title={d.isHoliday ? d.holidayName : undefined}
              >
                {d.display}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {filteredPrograms.map((prog, idx) => {
            const isHighlighted =
              highlightEventName && prog.eventName.toLowerCase().includes(highlightEventName.toLowerCase());

            return (
              <tr
                key={prog.softHoldId > 0 ? `${prog.isSoftHold ? "sh" : "dr"}-${prog.softHoldId}` : `ev-${idx}`}
                className={cn(
                  "border-b border-slate-100",
                  idx % 2 === 0 ? "bg-white" : "bg-[#F2F2F2]",
                  isHighlighted && "ring-2 ring-inset ring-amber-400"
                )}
              >
                {/* Link status icon */}
                <StickyTd width={COL_LINK} left={0}>
                  <LinkStatusIcon prog={prog} />
                </StickyTd>

                {/* Event status dot */}
                <StickyTd width={COL_STATUS} left={OFFSET_STATUS} className="text-center">
                  <span
                    className={cn("inline-block h-3 w-3 rounded-full", grcCss(prog.eventStatusCSSClass))}
                    title={prog.eventStatus}
                    aria-label={prog.eventStatus}
                  />
                </StickyTd>

                {/* Event name */}
                <StickyTd width={COL_NAME} left={OFFSET_NAME} className="max-w-[210px] pl-1">
                  <div
                    className={cn(
                      "cursor-default select-none truncate",
                      (prog.isSoftHold || prog.isDraft) && "cursor-pointer"
                    )}
                    title={prog.eventName}
                    onDoubleClick={() => onEventNameDoubleClick(prog)}
                    onClick={() => { if (prog.isDraft) onDraftEventClick(prog); }}
                  >
                    <span className="block truncate text-slate-900">{prog.eventName}</span>
                    {prog.isSoftHold && prog.softHoldCreatedDate && (
                      <span className="block text-[10px] text-slate-500">
                        Added: {prog.softHoldCreatedDate} by {prog.organizerName}
                      </span>
                    )}
                  </div>
                </StickyTd>

                {/* Event ID */}
                <StickyTd width={COL_ID} left={OFFSET_ID} className="text-center">
                  <span className="truncate">{prog.eventId}</span>
                </StickyTd>

                {/* Room block cells */}
                {days.map((d) => {
                  const block = prog.roomBlockDtos[String(d.day)];
                  return (
                    <td
                      key={d.day}
                      className={cn(
                        "w-[38px] border-l border-slate-100 py-1 text-center",
                        block && grcCss(block.cellCssForSoftHold),
                        block?.blocked === 0 && "text-slate-300"
                      )}
                    >
                      {block && block.blocked > 0 ? block.blocked : ""}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>

        <tfoot className="sticky bottom-0 z-20 border-t-2 border-slate-300 bg-white font-medium">
          {/* Blocked row */}
          <tr>
            <StickyTd width={COL_LINK} left={0} />
            <StickyTd width={COL_STATUS} left={OFFSET_STATUS} />
            <StickyTd width={COL_NAME} left={OFFSET_NAME} className="pl-1 text-slate-700">
              Blocked
            </StickyTd>
            <StickyTd width={COL_ID} left={OFFSET_ID} />
            {days.map((d) => (
              <td
                key={d.day}
                className={cn("py-1 text-center", grcCss(d.totalBlockedCountCss))}
              >
                {d.totalBlockedCount > 0 ? d.totalBlockedCount : "-"}
              </td>
            ))}
          </tr>

          {/* Pickup rows – shown only when showPickups */}
          {showPickups && (
            <>
              <FooterLabelRow
                label="Night Guest Pickup"
                days={days}
                getValue={(d) => d.totalNightGuestsPickup}
              />
              <FooterLabelRow
                label="Day Guests"
                days={days}
                getValue={(d) => d.totalDayGuestsCount}
              />
              <FooterLabelRow
                label="Offsite Guests"
                days={days}
                getValue={(d) => d.totalOffSiteGuestsCount}
              />
            </>
          )}

          {/* Available to sell */}
          <tr>
            <StickyTd width={COL_LINK} left={0} />
            <StickyTd width={COL_STATUS} left={OFFSET_STATUS} />
            <StickyTd width={COL_NAME} left={OFFSET_NAME} className="pl-1 text-slate-700">
              Available to sell{" "}
              <span className="font-normal text-slate-400">(incl. soft holds)</span>
            </StickyTd>
            <StickyTd width={COL_ID} left={OFFSET_ID} />
            {days.map((d) => (
              <td
                key={d.day}
                className={cn("py-1 text-center", grcCss(d.availableToSellcss))}
              >
                {d.availableToSell}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

interface StickyThProps {
  width: number;
  left: number;
  className?: string;
  children?: React.ReactNode;
}

function StickyTh({ width, left, className, children }: StickyThProps) {
  return (
    <th
      scope="col"
      className={cn("sticky z-20 border-r border-slate-200 bg-white py-1", className)}
      style={{ width, minWidth: width, left }}
    >
      {children}
    </th>
  );
}

interface StickyTdProps {
  width: number;
  left: number;
  className?: string;
  children?: React.ReactNode;
}

function StickyTd({ width, left, className, children }: StickyTdProps) {
  return (
    <td
      className={cn("sticky z-10 border-r border-slate-200 bg-inherit py-1", className)}
      style={{ width, minWidth: width, left }}
    >
      {children}
    </td>
  );
}

interface FooterLabelRowProps {
  label: string;
  days: GRCReportGridResponse["daysOfMonth"];
  getValue: (d: GRCReportGridResponse["daysOfMonth"][number]) => number;
}

function FooterLabelRow({ label, days, getValue }: FooterLabelRowProps) {
  return (
    <tr>
      <StickyTd width={COL_LINK} left={0} />
      <StickyTd width={COL_STATUS} left={OFFSET_STATUS} />
      <StickyTd width={COL_NAME} left={OFFSET_NAME} className="pl-1 text-slate-600 text-[11px]">
        {label}
      </StickyTd>
      <StickyTd width={COL_ID} left={OFFSET_ID} />
      {days.map((d) => {
        const v = getValue(d);
        return (
          <td key={d.day} className="py-0.5 text-center text-[11px] text-slate-500">
            {v > 0 ? v : ""}
          </td>
        );
      })}
    </tr>
  );
}

/** SVG link-status icon matching Blazor's LinkStatusIcon component */
function LinkStatusIcon({ prog }: { prog: ProgramDetailDto }) {
  if (!prog.isSoftHold) return null;

  const hasEvent = prog.eventId && prog.eventId.length > 0;
  const isConfirmed = prog.isSoftHoldConfirmed;

  if (!hasEvent) {
    // Broken link — grey
    return (
      <svg
        viewBox="0 0 24 24"
        className="mx-auto h-4 w-4 text-slate-400"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        aria-label="Unlinked soft hold"
      >
        <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
        <line x1="2" y1="2" x2="22" y2="22" />
      </svg>
    );
  }

  // Linked — green if confirmed, black if not
  const color = isConfirmed ? "#358225" : "black";
  return (
    <svg
      viewBox="0 0 24 24"
      className="mx-auto h-4 w-4"
      fill="none"
      stroke={color}
      strokeWidth={2}
      aria-label={isConfirmed ? "Linked and confirmed" : "Linked, unconfirmed"}
    >
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
    </svg>
  );
}
